import { DeleteOutline, Warning } from "@mui/icons-material"
import { Typography, Box } from "@mui/material"
import { ReactElement } from "react"

import { SUPPORT_EMAIL } from "../../errors/errorMessages"
import { useGetUserData } from "../../hooks/useGetUserData"
import { IAlert } from "../../services/middleware/mappers/singleDeviceMapper"
import { formatDateTime } from "../../utils/general/generalUtils"
import AppButton from "../buttons/appButton"
import AppAlert from "../dataDisplay/alerts/appAlert"
import VerticalKeyValueList from "../dataDisplay/verticalKeyValueList"
import { AppCardHeader } from "../surfaces/appCard"

interface AlertDisplayComponentProps {
    canDeleteAlerts: boolean
    alert: IAlert
    deleteAlert: () => void
}

export const AlertDisplayComponent = ({
    canDeleteAlerts,
    alert,
    deleteAlert,
}: AlertDisplayComponentProps): ReactElement => {
    const { isManuAdmin } = useGetUserData()

    const nextStepsMessage = isManuAdmin
        ? "The manufacturer has been advised and is working with the platform owner to assess and resolve the issue"
        : [
              "Please investigate the reported issue and get in touch with the Connect Direct admin when you think it is ready to be closed.",
              `You can do this by raising a ticket on the ${SUPPORT_EMAIL}`,
          ].join(" ")

    const formattedDate = formatDateTime(alert.raisedAt)
    const alerterName =
        alert.alerterGivenName && alert.alerterFamilyName
            ? `${alert?.alerterGivenName} ${alert.alerterFamilyName}`
            : "Unknown"

    return (
        <AppAlert
            severity="error"
            icon={false}
            action={
                canDeleteAlerts ? (
                    <AppButton color="inherit" onClick={deleteAlert}>
                        <Typography>Delete Alert</Typography>
                        <DeleteOutline style={{ marginLeft: "4px" }} />
                    </AppButton>
                ) : (
                    false
                )
            }
        >
            <AppCardHeader
                title={
                    <Box
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                        }}
                    >
                        <Warning
                            style={{
                                marginRight: "12px",
                            }}
                        />
                        An alert has been raised for this device
                    </Box>
                }
                titleTypographyProps={{
                    variant: "h5",
                    fontWeight: "500",
                }}
            />
            <VerticalKeyValueList
                kwargs={{
                    "Raised by": alerterName,
                    ...(alert.alerterOrganisation && {
                        Organisation: alert.alerterOrganisation,
                    }),
                    ...(alert.raisedAt && {
                        "Raised at": formattedDate,
                    }),
                    "Alert details": alert.description,
                    "Next steps": nextStepsMessage,
                }}
            />
        </AppAlert>
    )
}
